import { defineComponent, PropType } from 'vue'
import { Subject } from 'rxjs'
import { first, map } from 'rxjs/operators'
import { faCircleNotch, faBarcode, faLockOpen, faLock } from '@fortawesome/free-solid-svg-icons'

import { MovementTypes, Container, ContainerStatus } from '@skinsoft/types'
import { containerSelectors } from '@skinsoft/entity-ui'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { AllItemDisplayForm } from '../../all-item-display-form'

export interface Values {
    location: string
    identifier: string
}

export const AddContainerForm = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        allItemDisplayForm: AllItemDisplayForm
    },
    props: {
        onSubmit: () => {},
        initialValues: Object,
        movementType: String,
        canChainAdd: Boolean,
        statusesToSearch: {
            type: Array as PropType<ContainerStatus[]>,
            required: false,
        },
        excludedIds: Array,
        excludedItemsWarningMessageKey: {
            type: String,
            default: 'addContainerModal.errors.resultsAmongExcludedIds'
        },
    },
    template: require('./add-container-form.html'),
    data() {
        return {
            container: null as any as Container,

            containerSelectors,

            MovementTypes,

            searchTextValue$: new Subject(),
            searchText: '',
            pending: true,
            pageSize: 15,
            containers: [] as Container[],
            isBarcodeReaderActive: false,
            maxItemNumber: 0,
            currentPageIndex: 0,
            errorMessage: '' as String,

            INPUT_FILTER_IDENTIFIER: 'add-item-search-query-input',

            // Icons
            loadingIcon: faCircleNotch,
            barcodeIcon: faBarcode,
            openLockIcon: faLockOpen,
            closedLockIcon: faLock,
        }
    },
    computed: {
        lastPageIndex(): number {
            return Math.ceil(this.maxItemNumber / this.pageSize)
        },
    },
    methods: {
        subscribeToContainers(searchText = '', pageNumber = 0) {
            this.unsubscribeFrom('containers-subscription')
            this.pending = true

            let search$ = this.$containerDataStore.searchAll(
                {
                    queryText: searchText,
                    pageSize: this.pageSize,
                    page: this.currentPageIndex,
                },
                undefined,
                undefined,
                undefined,
                undefined,
                true,
            )
            if(this.statusesToSearch) {
                search$ = this.$containerDataStore.searchAmongStatusList(
                        searchText,
                        this.pageSize,
                        this.currentPageIndex,
                        this.statusesToSearch,
                        undefined,
                        [],
                    )
            }

            this.subscribeTo(
                search$
                    .pipe(
                        first(),
                        map(result => {
                            let excludedContainersThatMatched = [] as Container[] // used to inform user that the container is already in the movement/container

                            const filteredContainers = result.allData.filter(container => {
                                if(this.excludedIds?.includes(container.id)) {
                                    excludedContainersThatMatched.push(container)
                                    return false
                                } else {
                                    return true
                                }
                            })

                            return {
                                totalSize: filteredContainers.length,
                                allData: filteredContainers,
                                excludedContainersThatMatched
                            }
                        }),
                    ),
                (searchResponse) => {
                    this.maxItemNumber = searchResponse.totalSize
                    this.containers = searchResponse.allData
                    this.pending = false

                    if(searchResponse.excludedContainersThatMatched && searchResponse.excludedContainersThatMatched.length) {
                        this.errorMessage = this.$t(this.excludedItemsWarningMessageKey) as string
                    } else {
                        this.errorMessage = ''
                    }
                },
                'containers-subscription',
            )
        },

        incrementPageIndex() {
            this.currentPageIndex =
                this.currentPageIndex + 1 > this.lastPageIndex
                    ? this.lastPageIndex
                    : this.currentPageIndex + 1
            this.unsubscribeFrom('containers-subscription')
            this.subscribeToContainers()
        },

        decrementPageIndex() {
            this.currentPageIndex = this.currentPageIndex - 1 < 0 ? 0 : this.currentPageIndex - 1
            this.unsubscribeFrom('containers-subscription')
            this.subscribeToContainers()
        },

        toggleBarcodeReader() {
            this.isBarcodeReaderActive = !this.isBarcodeReaderActive
        },

        onBarcodeReaderInput(value: string) {
            this.searchText = value
            this.isBarcodeReaderActive = false
        },

        onItemSelected(container, index) {
            this.$emit('container-selected', { id: container.id, type: container.type })
        },
    },
    watch: {
        searchText() {
            this.searchTextValue$.next(this.searchText)
        },
    },
})
